import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, extractErrorCode } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useImperativeLoad } from "@/hooks/useImperativeLoad";
import {
  Users,
  UserPlus,
  Crown,
  Loader2,
  Mail,
  X,
  Trash2,
  LogOut,
  Activity as ActivityIcon,
  Sparkles,
  AlertTriangle,
} from "lucide-react";

export default function TeamPage() {
  const { t } = useI18n();
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [teamName, setTeamName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/team/me");
      setData(r.data);
      if (r.data.team) {
        const f = await api.get("/team/feed", { params: { limit: 30 } });
        setFeed(f.data.events);
      }
    } finally { setLoading(false); }
  };

  useImperativeLoad(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!teamName.trim()) return;
    setCreating(true);
    try {
      await api.post("/team/create", { name: teamName.trim() });
      toast.success(t("team.created"));
      setTeamName("");
      await refresh();
      load();
    } catch (err) {
      const code = extractErrorCode(err);
      toast.error(t(`team.err_${code}`, { default: t("team.create_failed") }));
    } finally { setCreating(false); }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      await api.post("/team/invite", { email: inviteEmail.trim() });
      toast.success(t("team.invited"));
      setInviteEmail("");
      load();
    } catch (err) {
      const code = extractErrorCode(err);
      toast.error(t(`team.err_${code}`, { default: t("team.invite_failed") }));
    } finally { setInviting(false); }
  };

  const revokeInvite = async (invite_id) => {
    await api.post(`/team/invites/${invite_id}/revoke`);
    toast.success(t("team.invite_revoked"));
    load();
  };

  const removeMember = async (user_id) => {
    if (!window.confirm(t("team.remove_confirm"))) return;
    await api.delete(`/team/members/${user_id}`);
    toast.success(t("team.removed"));
    load();
  };

  const leaveTeam = async () => {
    if (!window.confirm(t("team.leave_confirm"))) return;
    await api.post("/team/leave");
    toast.success(t("team.left"));
    await refresh();
    load();
  };

  const deleteTeam = async () => {
    if (!window.confirm(t("team.delete_confirm"))) return;
    await api.delete("/team");
    toast.success(t("team.deleted"));
    await refresh();
    load();
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  // Not on Business → upsell
  if (!data?.team && user?.subscription_tier !== "business") {
    return (
      <div className="px-4 py-10 sm:px-6 lg:px-12" data-testid="team-upsell">
        <header className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9ACD32]">{t("team.upsell_pretitle")}</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            {t("team.upsell_title")}
          </h1>
          <p className="mt-2 max-w-2xl text-slate-300">{t("team.upsell_subtitle")}</p>
        </header>
        <Card className="border-slate-800 bg-[#0A0C1F] p-8">
          <div className="flex items-start gap-4">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#9ACD32]/15 text-[#9ACD32]">
              <Users className="h-6 w-6" />
            </span>
            <div className="flex-1">
              <h2 className="font-display text-xl font-bold text-white">{t("team.upsell_card_title")}</h2>
              <p className="mt-2 text-sm text-slate-300">{t("team.upsell_card_desc")}</p>
              <ul className="mt-4 space-y-1.5 text-sm text-slate-300">
                <li>• {t("team.upsell_feat_1")}</li>
                <li>• {t("team.upsell_feat_2")}</li>
                <li>• {t("team.upsell_feat_3")}</li>
              </ul>
              <Link to="/pricing">
                <Button className="mt-5 rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]" data-testid="team-upgrade-btn">
                  <Sparkles className="mr-2 h-4 w-4" />
                  {t("team.upsell_cta")}
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // No team yet — show create form (Business owner)
  if (!data?.team) {
    return (
      <div className="px-4 py-10 sm:px-6 lg:px-12" data-testid="team-create-page">
        <header className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9ACD32]">{t("team.pretitle")}</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            {t("team.create_title")}
          </h1>
          <p className="mt-2 max-w-2xl text-slate-300">{t("team.create_subtitle")}</p>
        </header>
        <Card className="border-slate-800 bg-[#0A0C1F] p-6 sm:p-8 max-w-xl">
          <form onSubmit={handleCreate} className="space-y-4">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{t("team.field_name")}</span>
              <Input
                value={teamName}
                onChange={(e) => setTeamName(e.target.value)}
                placeholder={t("team.field_name_placeholder")}
                required
                minLength={2}
                className="mt-1.5 border-slate-700 bg-slate-900 text-slate-100"
                data-testid="team-name-input"
              />
            </label>
            <Button
              type="submit"
              disabled={creating || !teamName.trim()}
              className="rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
              data-testid="team-create-btn"
            >
              {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Users className="mr-2 h-4 w-4" />}
              {t("team.create_btn")}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  // Team exists — full dashboard
  const team = data.team;
  const isOwner = data.is_owner;
  const memberSlotsLeft = data.max_members - data.members.length - data.pending_invites.length;

  return (
    <div className="px-4 py-10 sm:px-6 lg:px-12" data-testid="team-dashboard">
      <header className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9ACD32]">{t("team.pretitle")}</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="team-name-heading">
            {team.name}
          </h1>
          <p className="mt-2 text-sm text-slate-300">
            {data.members.length} / {data.max_members} {t("team.members_count")}
          </p>
        </div>
        {!isOwner && (
          <Button onClick={leaveTeam} variant="outline" className="rounded-full border-slate-700 text-slate-200" data-testid="team-leave-btn">
            <LogOut className="mr-2 h-4 w-4" /> {t("team.leave_btn")}
          </Button>
        )}
      </header>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Members */}
        <Card className="border-slate-800 bg-[#0A0C1F] p-6 lg:col-span-2" data-testid="team-members-card">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-[#9ACD32]" />
            <h2 className="font-display text-lg font-bold text-white">{t("team.members_title")}</h2>
          </div>

          <ul className="mt-5 divide-y divide-slate-800">
            {data.members.map((m) => (
              <li key={m.user_id} className="flex items-center justify-between gap-3 py-3" data-testid={`team-member-${m.user_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-800 text-xs font-bold text-slate-200">
                    {(m.name || m.email).slice(0, 2).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-white">{m.name || m.email.split("@")[0]}</p>
                    <p className="truncate text-xs text-slate-500">{m.email}</p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {m.role === "owner" ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#9ACD32]/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#9ACD32]">
                      <Crown className="h-3 w-3" /> {t("team.role_owner")}
                    </span>
                  ) : (
                    <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      {t("team.role_member")}
                    </span>
                  )}
                  {isOwner && m.role !== "owner" && (
                    <button
                      onClick={() => removeMember(m.user_id)}
                      className="rounded-full p-1.5 text-slate-500 hover:bg-red-500/10 hover:text-red-400"
                      aria-label="Remove"
                      data-testid={`team-remove-${m.user_id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </li>
            ))}

            {data.pending_invites.map((inv) => (
              <li key={inv.invite_id} className="flex items-center justify-between gap-3 py-3" data-testid={`team-invite-${inv.invite_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-800 text-slate-500">
                    <Mail className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-300">{inv.email}</p>
                    <p className="truncate text-xs text-slate-500">{t("team.invite_pending")}</p>
                  </div>
                </div>
                {isOwner && (
                  <button
                    onClick={() => revokeInvite(inv.invite_id)}
                    className="rounded-full p-1.5 text-slate-500 hover:bg-red-500/10 hover:text-red-400"
                    aria-label="Revoke"
                    data-testid={`team-revoke-${inv.invite_id}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>

          {/* Invite form (owner only, capacity left) */}
          {isOwner && memberSlotsLeft > 0 && (
            <form onSubmit={handleInvite} className="mt-6 flex flex-col gap-2 sm:flex-row" data-testid="team-invite-form">
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder={t("team.invite_placeholder")}
                className="flex-1 border-slate-700 bg-slate-900 text-slate-100"
                data-testid="team-invite-input"
              />
              <Button
                type="submit"
                disabled={inviting || !inviteEmail.trim()}
                className="rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
                data-testid="team-invite-submit"
              >
                {inviting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserPlus className="mr-2 h-4 w-4" />}
                {t("team.invite_btn")}
              </Button>
            </form>
          )}
          {isOwner && memberSlotsLeft <= 0 && (
            <p className="mt-6 text-xs text-amber-400">{t("team.full")}</p>
          )}

          {/* Owner danger zone */}
          {isOwner && (
            <div className="mt-8 border-t border-red-900/30 pt-5">
              <p className="text-xs font-bold uppercase tracking-wider text-red-400">{t("team.danger_title")}</p>
              <p className="mt-1 text-xs text-slate-500">{t("team.danger_desc")}</p>
              <Button
                onClick={deleteTeam}
                variant="outline"
                size="sm"
                className="mt-3 rounded-full border-red-900 text-red-300 hover:bg-red-950/40"
                data-testid="team-delete-btn"
              >
                <AlertTriangle className="mr-2 h-3.5 w-3.5" />
                {t("team.delete_btn")}
              </Button>
            </div>
          )}
        </Card>

        {/* Team feed = Magic Inbox */}
        <Card className="border-slate-800 bg-[#0A0C1F] p-6" data-testid="team-feed-card">
          <div className="flex items-center gap-3">
            <ActivityIcon className="h-5 w-5 text-[#9ACD32]" />
            <h2 className="font-display text-lg font-bold text-white">{t("team.feed_title")}</h2>
          </div>
          <p className="mt-2 text-xs text-slate-500">{t("team.feed_desc")}</p>
          {feed.length === 0 ? (
            <p className="mt-6 rounded-lg border border-slate-800 bg-slate-950/40 p-4 text-center text-sm text-slate-500" data-testid="team-feed-empty">
              {t("team.feed_empty")}
            </p>
          ) : (
            <ul className="mt-5 space-y-3 max-h-[28rem] overflow-y-auto">
              {feed.map((e) => (
                <li key={e.event_id} className="rounded-lg border border-slate-800 bg-slate-950/40 px-3 py-2.5 text-sm">
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-semibold text-slate-200">{e.actor_name}</span>
                    <span className="text-[10px] text-slate-500">{formatRelative(e.created_at)}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-400">{t(`activity.event.${e.type}.title`, { default: e.type })}</p>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function formatRelative(iso) {
  try {
    const t = (Date.now() - new Date(iso).getTime()) / 1000;
    if (t < 60) return `${Math.floor(t)}s`;
    if (t < 3600) return `${Math.floor(t / 60)}m`;
    if (t < 86400) return `${Math.floor(t / 3600)}h`;
    return `${Math.floor(t / 86400)}d`;
  } catch (e) { return ""; }
}
